import { useEffect } from "react";

import type { SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { toastSuccessMessage } from "utils/alerts";
import { BROADCAST_TOPICS, addBroadcastListener } from "utils/broadcast";

const pendingCloneCountsByProgenitorId = new Map<string, number>();

/** Register a progenitor section id so a later pubsub create toasts when the clone arrives. */
export function watchForClonedSection(progenitorId: string) {
  pendingCloneCountsByProgenitorId.set(
    progenitorId,
    (pendingCloneCountsByProgenitorId.get(progenitorId) ?? 0) + 1,
  );
}

/** Visible for tests — drops pending clone watches without unmounting the observer. */
export function clearSectionCloneWatchers() {
  pendingCloneCountsByProgenitorId.clear();
}

/**
 * Listens for section-create pubsub while the home page is mounted. Pair with
 * `watchForClonedSection` after starting a clone; unmounting home clears both the
 * listener and any outstanding watches.
 */
export function useSectionCloneObserver() {
  useEffect(() => {
    const unsubscribe = addBroadcastListener(BROADCAST_TOPICS.PUBSUB_MODEL_UPDATE, (message) => {
      if (
        message.type !== "model-update" ||
        message.model !== "sections" ||
        message.action !== "create"
      ) {
        return;
      }

      const section = message.data as SectionType;
      const progenitorId = section.progenitor_id;
      if (!progenitorId) {
        return;
      }

      const remaining = pendingCloneCountsByProgenitorId.get(progenitorId);
      if (!remaining) {
        return;
      }

      if (remaining === 1) {
        pendingCloneCountsByProgenitorId.delete(progenitorId);
      } else {
        pendingCloneCountsByProgenitorId.set(progenitorId, remaining - 1);
      }

      toastSuccessMessage(t("success.toasts.clone_section_success", { title: section.title }));
    });

    return () => {
      unsubscribe();
      pendingCloneCountsByProgenitorId.clear();
    };
  }, []);
}
